// =============================================================================
// The back-button service must see every popstate BEFORE Angular does.
//
// Listeners on window run in registration order within each phase (capture
// listeners do run first at the target since Chrome 89). The catch is zone.js:
// it funnels every zone listener of one kind and phase through ONE native
// listener, created when the first such listener is added.
// @syncfusion/ej2-base adds a popstate listener while its module loads, so
// Angular's Location listener (added later) rides on that early native
// listener; measured in this app, it ran before a capture listener added later. Registering ours first, in polyfills.ts, makes
// the order explicit instead of depending on the capture-phase rule.
//
// So polyfills.ts installs this listener right after zone.js, before any app
// or vendor module runs. It can then stopImmediatePropagation() a pop that
// only closed a dialog, and the router never hears about it.
//
// State lives on window, not in module scope, so it is shared even if the
// polyfills and main bundles each carry a copy of this module.
// =============================================================================

type PopHandler = (event: PopStateEvent) => void;

interface PopSlot {
  installed: boolean;
  handler: PopHandler | null;
}

const SLOT_KEY = '__fnBackButtonPopstate';

function slot(): PopSlot | null {
  if (typeof window === 'undefined') {
    return null;
  }
  const w = window as unknown as Record<string, PopSlot | undefined>;
  if (!w[SLOT_KEY]) {
    w[SLOT_KEY] = { installed: false, handler: null };
  }
  return w[SLOT_KEY] as PopSlot;
}

export function installBackButtonPopstateListener(): void {
  const s = slot();
  if (!s || s.installed || typeof window.addEventListener !== 'function') {
    return;
  }
  s.installed = true;
  window.addEventListener('popstate', (event) => s.handler?.(event), true);
}

export function setBackButtonPopstateHandler(next: PopHandler | null): void {
  const s = slot();
  if (s) {
    s.handler = next;
  }
}

export function getBackButtonPopstateHandler(): PopHandler | null {
  return slot()?.handler ?? null;
}
